import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useTranslation } from 'react-i18next';
import { ControllerWrapper } from '../../../../hooks/providers/ControllerWrapper';
import { ExtendedUseFormReturn } from '../../../../hooks/providers/useFormHooks';
import { LiliTextInput } from '../../../../DesignSystem/LiliTextInput/LiliTextInput';
import Heading from '../../../../generic-components/Heading';
import { ReactComponent as DownArrowIcon } from '../../../../assets/icons/svg/shared/ic-shared-down-arrow.svg';
import { UpdateVendorFormFieldsMap, UpdateVendorFormProps } from '../../interfaces';
import { useInternationalAddressFieldLogic } from './hooks/useInternationalAddressFieldLogic';

interface AddressFieldsProps {
  formMethods: ExtendedUseFormReturn<UpdateVendorFormProps>;
  dynamicCountry: string;
}

export const InternationalAddressFields = (props: AddressFieldsProps) => {
  const {
    formMethods: { control },
    formMethods,
    dynamicCountry,
  } = props;
  const { t } = useTranslation();

  const { selectedCountry, getZipCodeHint } = useInternationalAddressFieldLogic(formMethods, dynamicCountry);

  return (
    <>
      <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_LINE_1} control={control}>
        {({ onBlur, onChange, error, value }) => (
          <LiliTextInput
            onChange={onChange}
            value={value}
            onBlur={onBlur}
            id={UpdateVendorFormFieldsMap.INTER_LINE_1}
            title={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.LINE_1')}
            disabled={!dynamicCountry}
            errorMessage={error?.message}
          />
        )}
      </ControllerWrapper>

      <div className='flex items-center w-full gap-[24px]'>
        <div className='w-full'>
          <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_LINE_2} control={control}>
            {({ onBlur, error, value, onChange }) => (
              <LiliTextInput
                onChange={onChange}
                value={value}
                disabled={!dynamicCountry}
                onBlur={onBlur}
                id={UpdateVendorFormFieldsMap.INTER_LINE_2}
                title={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.LINE_2')}
                errorMessage={error?.message}
              />
            )}
          </ControllerWrapper>
        </div>

        <div className='w-full'>
          <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_CITY} control={control}>
            {({ onBlur, onChange, error, value }) => (
              <LiliTextInput
                onChange={onChange}
                value={value}
                disabled={!dynamicCountry}
                onBlur={onBlur}
                id={UpdateVendorFormFieldsMap.INTER_CITY}
                title={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.CITY')}
                errorMessage={error?.message}
              />
            )}
          </ControllerWrapper>
        </div>
      </div>

      <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_STATE} control={control}>
        {({ onChange, value, onBlur }) => (
          <div className='min-h-[76px] w-full'>
            <Dropdown
              value={value}
              onBlur={onBlur}
              onChange={(e) => onChange(e.value)}
              options={selectedCountry?.subDivisions || []}
              optionLabel='name'
              optionValue='code'
              itemTemplate={(item) => <Heading title={item.name} className='font-metricR text-bw08' />}
              placeholder={t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.REGION')}
              className={`vendorCustomDropDown ${!dynamicCountry || !selectedCountry?.subDivisions?.length ? 'pointer-events-none bg-bw02' : ''}`}
              panelClassName='vendorDropDown rounded-[8px] shadow-2xl'
              dropdownIcon={<DownArrowIcon className={`text-action03 ${selectedCountry?.subDivisions?.length ? '' : 'hidden'}`} />}
            />
          </div>
        )}
      </ControllerWrapper>

      <ControllerWrapper name={UpdateVendorFormFieldsMap.INTER_POSTAL_CODE} control={control}>
        {({ onBlur, onChange, error, value }) => (
          <LiliTextInput
            onChange={onChange}
            value={value}
            maxLength={10}
            onBlur={onBlur}
            disabled={!dynamicCountry}
            id={UpdateVendorFormFieldsMap.INTER_POSTAL_CODE}
            title={`${t('MOVE_MONEY.STEPS.VENDOR_MANAGER.ADDITIONAL_FIELDS.POSTAL')} ${getZipCodeHint()}`}
            errorMessage={error?.message}
          />
        )}
      </ControllerWrapper>
    </>
  );
};
